import { useState } from "react";
import type { RendererBranding } from "./types.js";

/**
 * A star rating that shows stars.
 *
 * It replaces an `<input type="number">`, which was wrong in three separate ways: it did not look
 * like a rating, it accepted a typed `-4`, and on a phone it opened a numeric keypad to answer
 * "how would you rate the workshop".
 *
 * Built on native radios rather than `role="radio"` divs. That is what gives arrow-key navigation,
 * roving focus and the "3 of 5 stars" announcement for free, correctly, on screen readers this
 * project will never get to test against. Each radio is a transparent overlay filling its own hit
 * zone — it is never `display:none`, so it still takes focus and still gets a visible ring.
 */

const StarGlyph = ({ className, colour }: { className: string; colour: string }): JSX.Element => (
  <svg className={className} viewBox="0 0 24 24" fill={colour} aria-hidden="true" focusable="false">
    <path d="M12 2.6l2.9 5.88 6.49.94-4.7 4.58 1.11 6.46L12 17.4l-5.8 3.06 1.1-6.46-4.69-4.58 6.49-.94L12 2.6z" />
  </svg>
);

/** One star, filled 0, 0.5 or 1 of the way across. */
const Star = ({
  fill,
  colour,
  empty,
}: {
  fill: number;
  colour: string;
  empty: string;
}): JSX.Element => (
  <span aria-hidden="true" className="pointer-events-none absolute inset-0">
    <StarGlyph className="absolute inset-0 h-full w-full" colour={empty} />
    {fill > 0 && (
      // Clipped by width rather than drawn with an SVG gradient: no generated element ids, so two
      // rating fields on one page cannot collide.
      <span
        className="absolute inset-y-0 left-0 overflow-hidden"
        style={{ width: `${fill * 100}%` }}
      >
        <StarGlyph className="h-full w-7" colour={colour} />
      </span>
    )}
  </span>
);

export interface StarRatingProps {
  /** Radio group name — must be unique on the page, so it carries the field's own input id. */
  name: string;
  /** Put on the first star, so the field's visible `<label htmlFor>` resolves to a real control. */
  id?: string;
  starCount: number;
  allowHalf: boolean;
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  required?: boolean;
  describedBy?: string;
  labelledBy: string;
  branding: RendererBranding;
}

export const StarRating = ({
  name,
  id,
  starCount,
  allowHalf,
  value,
  onChange,
  disabled,
  required,
  describedBy,
  labelledBy,
  branding,
}: StarRatingProps): JSX.Element => {
  // Hover previews the rating. It must never survive the pointer leaving the row, or the stars
  // would disagree with the radio that is actually checked.
  const [hovered, setHovered] = useState<number | null>(null);
  const shown = hovered ?? value ?? 0;

  const stars = Array.from({ length: starCount }, (_, index) => index + 1);

  return (
    <div>
      <div
        role="radiogroup"
        aria-labelledby={labelledBy}
        aria-required={required || undefined}
        aria-describedby={describedBy || undefined}
        className="flex flex-wrap items-center gap-1"
        onMouseLeave={() => setHovered(null)}
      >
        {stars.map((starIndex) => (
          <span key={starIndex} className="relative block h-7 w-7 shrink-0">
            <Star
              fill={Math.min(Math.max(shown - (starIndex - 1), 0), 1)}
              colour={branding.primary}
              empty={branding.border}
            />

            {(allowHalf ? [starIndex - 0.5, starIndex] : [starIndex]).map((step, stepIndex) => {
              const isHalf = step !== starIndex;
              const isFirstInGroup = starIndex === 1 && stepIndex === 0;
              return (
                <label
                  key={step}
                  className="absolute inset-y-0"
                  style={{
                    left: allowHalf && !isHalf ? "50%" : 0,
                    width: allowHalf ? "50%" : "100%",
                  }}
                  onMouseEnter={() => !disabled && setHovered(step)}
                >
                  <input
                    type="radio"
                    id={isFirstInGroup ? id : undefined}
                    name={name}
                    value={step}
                    checked={value === step}
                    disabled={disabled}
                    onChange={() => onChange(step)}
                    onFocus={() => !disabled && setHovered(step)}
                    onBlur={() => setHovered(null)}
                    className={`peer absolute inset-0 h-full w-full opacity-0 ${
                      disabled ? "cursor-not-allowed" : "cursor-pointer"
                    }`}
                  />
                  <span className="sr-only">
                    {step} {step === 1 ? "star" : "stars"} out of {starCount}
                  </span>
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute -inset-0.5 rounded opacity-0 peer-focus-visible:opacity-100"
                    style={{ boxShadow: `0 0 0 2px ${branding.primary}` }}
                  />
                </label>
              );
            })}
          </span>
        ))}

        <span className="ml-2 text-sm tabular-nums" style={{ color: branding.text, opacity: 0.7 }}>
          {value === null ? "Not rated" : `${value} / ${starCount}`}
        </span>
      </div>

      {/* A radio group cannot be un-checked from the keyboard once set, and a rating is usually
          optional — without this a mis-click is permanent. */}
      {!required && value !== null && !disabled && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="mt-1 text-xs underline underline-offset-2"
          style={{ color: branding.text, opacity: 0.7 }}
        >
          Clear rating
        </button>
      )}
    </div>
  );
};
